import { addDays, differenceInCalendarDays, differenceInMonths, startOfDay } from 'date-fns';

import * as GLOBAL from 'constants/Global';
import { DAY, MONTH, QUARTER, WEEK, YEAR } from 'constants/StatisticsConstants';
import type { MayBe } from 'modules/global/typing/common/common';

export type GroupBy = typeof DAY | typeof WEEK | typeof MONTH | typeof QUARTER | typeof YEAR;

type PeriodRange = { from: string | Date; to: string | Date };

export type StatisticsPeriod = PeriodRange & { period: MayBe<string> };

export const GROUP_BY_ORDER: GroupBy[] = [DAY, WEEK, MONTH, QUARTER, YEAR];

export const GROUP_BY_API_SUFFIX = {
  [DAY]: 'YearMonthDay',
  [WEEK]: 'YearWeek',
  [MONTH]: 'YearMonth',
  [QUARTER]: 'YearQuarter',
  [YEAR]: 'Year',
} as const satisfies Record<GroupBy, string>;

export type GroupByApiSuffix = (typeof GROUP_BY_API_SUFFIX)[GroupBy];

/**
 * Returns the Group by options that are not meaningful for the given period range.
 */
export const getDisabledGroupBys = ({ from, to }: PeriodRange): Set<GroupBy> => {
  const start = startOfDay(from);
  const end = addDays(startOfDay(to), 1);
  const dayCount = differenceInCalendarDays(end, start);
  const disabled = new Set<GroupBy>();

  if (differenceInMonths(to, from) >= 3) {
    disabled.add(DAY);
  }
  if (dayCount <= 1) {
    disabled.add(WEEK);
  }
  if (dayCount < 7) {
    disabled.add(MONTH);
  }
  if (differenceInMonths(end, start) < 1) {
    disabled.add(QUARTER);
    disabled.add(YEAR);
  }

  return disabled;
};

/**
 * Returns the given Group by when allowed, otherwise the closest allowed one.
 */
export const getClosestAllowedGroupBy = (value: GroupBy, disabled: Set<GroupBy>): GroupBy => {
  const valueIndex = GROUP_BY_ORDER.indexOf(value);
  const allowedOptions = GROUP_BY_ORDER.filter(option => !disabled.has(option));

  return allowedOptions.findLast(option => GROUP_BY_ORDER.indexOf(option) <= valueIndex) ?? allowedOptions[0];
};

const PERIOD_PRESET_GROUP_BY = new Map<MayBe<string>, GroupBy>([
  [GLOBAL.MTD, WEEK],
  [GLOBAL.MONTH, WEEK],
  [GLOBAL.QUARTER, WEEK],
  [GLOBAL.YEAR, MONTH],
  [GLOBAL.YTD, MONTH],
]);

const getPresetGroupBy = (period: MayBe<string>): GroupBy => PERIOD_PRESET_GROUP_BY.get(period) ?? DAY;

/**
 * Returns the Group by to display: the one the user selected, or the period preset default when
 * none is selected yet, adjusted to an option allowed by the period range.
 */
export const getEffectiveGroupBy = (period: StatisticsPeriod, selectedGroupBy: MayBe<GroupBy>): GroupBy => {
  const requested = selectedGroupBy ?? getPresetGroupBy(period.period);
  return getClosestAllowedGroupBy(requested, getDisabledGroupBys(period));
};
